import { AlertTriangle, TrendingUp } from '../../components/ui/icons';
import { cn } from '../../lib/cn';
import { formatMultiplier, signedPoints } from '../../lib/format';
import type { LiveCardVM } from './useFootballMatchDetail';

/**
 * The watch-along hero: a big "if it ends now" points read for the card you built
 * on this live match. Green and banking when your 1X2 is landing, red and busting
 * when it's wrong — glanceable enough to keep on while you watch the game.
 */
export function LiveCardSummary({ live }: { live: LiveCardVM }) {
  const { points, baseHit, multiplier, boostsHitting, boostsTotal } = live;

  return (
    <div
      className={cn(
        'relative mt-3 overflow-hidden rounded-2xl px-[18px] py-[16px] ring-1',
        baseHit
          ? 'bg-[linear-gradient(135deg,#0E4A33,#08231A)] ring-accent/30'
          : 'bg-[linear-gradient(135deg,#3D1414,#1B0808)] ring-miss/30'
      )}
    >
      {/* halo glow in the card's mood colour */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-10 -top-12 h-[160px] w-[160px] rounded-full',
          baseHit
            ? 'bg-[radial-gradient(circle,rgba(36,197,106,0.28),transparent_70%)]'
            : 'bg-[radial-gradient(circle,rgba(239,65,53,0.24),transparent_70%)]'
        )}
      />
      {/* a slow sheen sweep while the card is banking */}
      {baseHit && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-[45%] animate-h2h-sheen bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)]"
        />
      )}

      <div className="relative flex items-center justify-between">
        <span className="inline-flex items-center gap-[6px]">
          <span
            className={cn(
              'h-[6px] w-[6px] animate-h2h-pulse rounded-full',
              baseHit ? 'bg-accent' : 'bg-miss'
            )}
          />
          <span
            className={cn(
              'text-[10px] font-extrabold uppercase tracking-eyebrow',
              baseHit ? 'text-accent' : 'text-miss'
            )}
          >
            Your card right now
          </span>
        </span>
        <span className="inline-flex items-baseline gap-[2px] rounded-lg bg-white/10 px-[9px] py-1 text-white">
          <span className="text-[10px] font-bold text-white/50">×</span>
          <span className="num text-[13px] font-extrabold leading-none">{formatMultiplier(multiplier)}</span>
        </span>
      </div>

      <div className="relative mt-[12px] flex items-end gap-[10px]">
        <span
          className={cn(
            'num text-[44px] font-extrabold leading-none tracking-tight',
            baseHit ? 'text-accent' : 'text-miss'
          )}
        >
          {signedPoints(points)}
        </span>
        <span className="mb-[5px] inline-flex items-center gap-[5px] text-[12px] font-bold text-white/80">
          {baseHit ? (
            <TrendingUp width={14} height={14} strokeWidth={2.6} className="text-accent" />
          ) : (
            <AlertTriangle width={14} height={14} strokeWidth={2.6} className="text-miss" />
          )}
          {baseHit ? 'banking if it ends now' : '1X2 wrong — busting'}
        </span>
      </div>

      {boostsTotal > 0 && (
        <div className="relative mt-[14px] flex items-center justify-between border-t border-white/10 pt-[12px]">
          <span className="text-[11px] font-semibold text-white/60">
            {baseHit
              ? `${boostsHitting} of ${boostsTotal} boost${boostsTotal === 1 ? '' : 's'} live`
              : 'Land the result to bank your boosts'}
          </span>
          <div className="flex gap-[4px]">
            {Array.from({ length: boostsTotal }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-[7px] w-[7px] rounded-full',
                  i < boostsHitting ? 'bg-accent shadow-[0_0_8px_rgba(36,197,106,0.7)]' : 'bg-white/20'
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
